import { ArrowRight, MessageCircle, ShieldCheck, Lock, FileCheck, BadgeCheck } from "lucide-react";
import { SITE } from "@/lib/site";

const HERO_BANNER = "https://customer-assets-jai6qajn.emergentagent.net/job_review-service/artifacts/8b9fdc6a293ab571_Online-Reputationsberaterin%20im%20modernen%20Bu%CC%88ro.png";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 lg:pt-40 pb-20 lg:pb-28 overflow-hidden subtle-grain"
    >
      {/* Subtle background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f5f1ea] via-[#f5f1ea] to-[#f5f1ea]" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#2b2620]/[0.07] blur-[160px]" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT: text on desktop, below banner on mobile */}
          <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#2b2620]/30 bg-[#2b2620]/5 text-[#2b2620] text-xs font-medium uppercase tracking-[0.18em] fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2b2620] animate-pulse" />
            Reputationsmanagement für Unternehmen
          </div>

          <h1
            className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight leading-[1.05] text-[#2b2620] fade-up"
            data-testid="hero-headline"
          >
            Unfaire Bewertungen?<br />
            <span className="gold-text">Strukturiert melden.</span>
          </h1>

          <p className="mt-5 text-[#4a3f36] text-xl sm:text-2xl lg:text-3xl font-normal leading-snug max-w-3xl fade-up" style={{ animationDelay: "60ms" }}>
            Ich unterstütze Unternehmen beim Meldeprozess über die offiziellen Wege von Google, Trustpilot &amp; Co.
          </p>

          <p className="mt-7 text-base sm:text-lg text-[#6b5f52] max-w-3xl leading-relaxed fade-up" style={{ animationDelay: "100ms" }}>
            Klar formuliert, diskret bearbeitet und transparent abgerechnet. Die Entscheidung über eine Entfernung liegt immer bei der jeweiligen Plattform – ich bereite Ihre Meldung professionell auf und reiche sie strukturiert ein.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 fade-up" style={{ animationDelay: "200ms" }}>
            <a
              href="#kontakt"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#2b2620] text-white font-semibold hover:bg-[#4a4038] transition-all"
            >
              Bewertung übermitteln
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window === "undefined") return;
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ event: "whatsapp_click", source: "hero_cta" });
                if (typeof window.gtag === "function") {
                  window.gtag("event", "conversion", { send_to: "AW-16867023480/7Cp6CKy784ocEPi06eo-" });
                }
              }}
              data-testid="hero-cta-whatsapp"
              data-conversion="whatsapp_click"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-black/10 text-[#2b2620] hover:bg-black/[0.03] transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              Unverbindlich per WhatsApp
            </a>
          </div>

          <p className="mt-5 text-xs text-[#8a7f70] max-w-xl">
            In der Regel Rückmeldung innerhalb von 24 Stunden &middot; SSL-verschlüsselte Anfrage &middot; Diskrete Bearbeitung
          </p>
          </div>

          {/* RIGHT: hero banner (portrait image) */}
          <div className="lg:col-span-5 order-1 lg:order-2 fade-up">
            <HeroBanner />
          </div>
        </div>

        {/* Trust bar */}
        <div className="mt-14 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/[0.03] border border-black/[0.08] rounded-2xl overflow-hidden">
          {[
            { icon: Lock, label: "SSL-verschlüsselt" },
            { icon: ShieldCheck, label: "DSGVO-konform" },
            { icon: FileCheck, label: "Festpreis – keine Abos" },
            { icon: BadgeCheck, label: "Diskrete Bearbeitung" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              data-testid={`trust-item-${label}`}
              className="flex items-center gap-3 px-5 py-5 bg-[#faf6ef]"
            >
              <Icon className="w-5 h-5 text-[#2b2620]" />
              <span className="text-sm text-[#3a3128] font-medium">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroBanner() {
  return (
    <div
      data-testid="hero-banner"
      className="relative overflow-hidden rounded-3xl border border-black/[0.08] bg-[#efe8dc] shadow-[0_30px_80px_-20px_rgba(43,38,32,0.35)]"
      style={{ aspectRatio: "4 / 5" }}
    >
      <img
        src={HERO_BANNER}
        alt="Online-Reputationsberaterin im modernen Büro"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "center 28%" }}
        loading="eager"
      />
    </div>
  );
}
